/**
 * Auth State Manager
 * Ported from: innovation-claude/poc/mfe-shared/src/auth_state_manager.rs
 *
 * Handles persistence of authentication state to sessionStorage.
 * Separation of concerns:
 * - AuthManager (future): Handles auth logic, token renewal, API calls
 * - AuthStateManager: Handles persistence only
 */

import { StorageEngine } from '@innolab-trader/storage'
import type { TradovateAuth } from './types'

const AUTH_STORAGE_KEY = 'tradovate-auth'

/**
 * AuthStateManager - sessionStorage persistence for authentication
 *
 * Storage key: "tradovate-auth"
 * Shared by: All MFEs (auth, workspace, etc.)
 *
 * Ported from auth_state_manager.rs:20-74
 */
export class AuthStateManager {
    /**
     * Load authentication from sessionStorage
     *
     * @returns TradovateAuth if found and valid, null otherwise
     */
    static async load(): Promise<TradovateAuth | null> {
        try {
            const engine = StorageEngine.session()
            const doc = engine.document<TradovateAuth>(AUTH_STORAGE_KEY)
            const auth = await doc.load()

            if (auth) {
                console.log(`✅ Loaded auth from storage: ${auth.name} (expires: ${auth.expirationTime})`)
            } else {
                console.log('ℹ️ No stored authentication')
            }

            return auth
        } catch (error) {
            console.warn('⚠️ Failed to load auth:', error)
            return null
        }
    }

    /**
     * Save authentication to sessionStorage
     *
     * @param auth - Authentication to persist
     */
    static async save(auth: TradovateAuth): Promise<void> {
        try {
            const engine = StorageEngine.session()
            const doc = engine.document<TradovateAuth>(AUTH_STORAGE_KEY)
            await doc.save(auth)

            console.log(`💾 Auth saved to storage: ${auth.name} (expires: ${auth.expirationTime})`)
        } catch (error) {
            console.error('⚠️ Failed to save auth:', error)
            throw error
        }
    }

    /**
     * Clear authentication from sessionStorage
     */
    static async clear(): Promise<void> {
        try {
            const engine = StorageEngine.session()
            const doc = engine.document<TradovateAuth>(AUTH_STORAGE_KEY)
            await doc.delete()

            console.log('🧹 Auth cleared from storage')
        } catch (error) {
            console.warn('⚠️ Failed to clear auth:', error)
        }
    }

    /**
     * Check if auth is valid (not expired)
     *
     * @param auth - Authentication to validate
     * @returns True if token is not expired
     */
    static isAuthValid(auth: TradovateAuth): boolean {
        const now = new Date()
        const expiration = new Date(auth.expirationTime)
        return expiration > now
    }

    /**
     * Check if auth is valid with buffer
     * Used for renewal scheduling (renew 5min before expiry)
     *
     * @param auth - Authentication to validate
     * @param bufferMinutes - Minutes before expiry to consider invalid (default 5)
     * @returns True if token won't expire within buffer
     */
    static isAuthValidWithBuffer(auth: TradovateAuth, bufferMinutes: number = 5): boolean {
        const now = new Date()
        const expiration = new Date(auth.expirationTime)
        const buffer = bufferMinutes * 60 * 1000  // Convert to milliseconds

        return expiration.getTime() - now.getTime() > buffer
    }

    /**
     * Get milliseconds until token expires
     *
     * @param auth - Authentication to check
     * @returns Milliseconds until expiry, or 0 if already expired
     */
    static getTimeUntilExpiration(auth: TradovateAuth): number {
        const now = new Date()
        const expiration = new Date(auth.expirationTime)
        const diff = expiration.getTime() - now.getTime()

        return Math.max(0, diff)
    }
}
